import { getPrefectureName, PREFECTURE_OPTIONS } from '@admission/shared';
import { ArrowRight, ArrowUpDown, Building2, Check, ChevronDown, ChevronUp, Search, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { apiUrl } from '@/config/api';
import { authGet } from '@/lib/authFetch';
import { cn } from '@/lib/utils';

interface Tenant {
  id: string;
  name: string;
  tenantId: string;
  tenant?: {
    name: string;
    isContracted?: string;
  };
  domain: string;
  type: string;
  institutionType?: string;
  status: 'active' | 'inactive';
  prefectureCode: string;
  region: string;
  mainBranchType?: string;
  abolitionDate?: string;
  mergerDate?: string;
  mergerTargetId?: string;
  nameKana?: string;
  founderName?: string;
  founderType?: string;
  establishmentDate?: string;
}

type SortField = 'name' | 'type' | 'prefectureCode' | 'establishmentDate';
type SortOrder = 'asc' | 'desc';

interface MasterOrganization {
  id: string;
  name: string;
  type: string;
  establishmentClass?: string;
  abolitionDate?: string;
  mergerDate?: string;
  mergerTargetId?: string;
}

/**
 * Returns Tailwind CSS classes for styling tenant type badges.
 * @param type - The institution type (national, public, private)
 * @returns CSS class string for badge styling
 */
const getTenantTypeStyles = (type: string) => {
  switch (type?.toLowerCase()) {
    case 'national':
      return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'public':
      return 'bg-green-50 text-green-700 border-green-200';
    case 'private':
      return 'bg-purple-50 text-purple-700 border-purple-200';
    default:
      return 'bg-gray-50 text-gray-700 border-gray-200';
  }
};

const getCandidateTypeStyles = (type: string) => getTenantTypeStyles(type);

/**
 * InstitutionList - Platform admin page for managing educational institutions.
 * Displays a searchable, filterable, sortable list of institutions with pagination.
 * Supports linking MEXT master data to tenant institutions.
 */
export default function InstitutionList() {
  const { t } = useTranslation();
  const [activeTenants, setActiveTenants] = useState<Tenant[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Filter & UI State
  // filterMode: 'contracted' | 'notContracted'
  const [filterMode, setFilterMode] = useState<'contracted' | 'notContracted'>('contracted');
  const [listSearch, setListSearch] = useState('');
  const [listPrefectures, setListPrefectures] = useState<string[]>([]);
  const [listType, setListType] = useState('');
  const [showAbolished, setShowAbolished] = useState(false);

  // Sorting State
  const [sortField, setSortField] = useState<SortField>('prefectureCode');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const PAGE_SIZES = [10, 20, 50, 100];

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchPrefecture, setSearchPrefecture] = useState('');
  const [searchType, setSearchType] = useState('');
  const [searchResults, setSearchResults] = useState<MasterOrganization[]>([]);
  const [selectedCandidate, setSelectedCandidate] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await authGet(apiUrl('/api/institutions'));
      if (res.ok) {
        const data = (await res.json()) as Tenant[];
        setActiveTenants(data);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  /**
   * Toggles a prefecture code in the multi-select prefecture filter.
   * @param code - Prefecture code to toggle
   */
  const togglePrefecture = (code: string) => {
    setListPrefectures((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));
    setCurrentPage(1);
  };

  const filteredAndSortedTenants = useMemo(() => {
    const filtered = activeTenants.filter((t) => {
      // Filter by contract status
      const isContracted = t.tenant?.isContracted === 'true';
      if (filterMode === 'contracted' && !isContracted) return false;
      if (filterMode === 'notContracted' && isContracted) return false;

      if (!showAbolished && t.abolitionDate) return false;
      if (listSearch && !t.name.toLowerCase().includes(listSearch.toLowerCase())) return false;
      if (listPrefectures.length > 0 && !listPrefectures.includes(t.prefectureCode)) return false;
      if (listType && t.type !== listType) return false;
      return true;
    });

    return [...filtered].sort((a, b) => {
      let comparison = 0;
      if (sortField === 'name') {
        comparison = (a.nameKana || a.name).localeCompare(b.nameKana || b.name, 'ja');
      } else if (sortField === 'prefectureCode') {
        comparison = (a.prefectureCode || '99').localeCompare(b.prefectureCode || '99');
        if (comparison === 0) {
          comparison = (a.nameKana || a.name).localeCompare(b.nameKana || b.name, 'ja');
        }
      } else if (sortField === 'establishmentDate') {
        comparison = (a.establishmentDate || '').localeCompare(b.establishmentDate || '');
      } else {
        const key = sortField as keyof Tenant;
        const valA = String(a[key] || '');
        const valB = String(b[key] || '');
        comparison = valA.localeCompare(valB);
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });
  }, [activeTenants, filterMode, showAbolished, listSearch, listPrefectures, listType, sortField, sortOrder]);

  const contractedCount = useMemo(
    () => activeTenants.filter((t) => t.tenant?.isContracted === 'true').length,
    [activeTenants],
  );
  const notContractedCount = useMemo(
    () => activeTenants.filter((t) => t.tenant?.isContracted !== 'true').length,
    [activeTenants],
  );

  const totalItems = filteredAndSortedTenants.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const paginatedTenants = filteredAndSortedTenants.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const getLifecycleStatus = (abolitionDate?: string) => {
    if (!abolitionDate) return '---';
    const now = new Date();
    const date = new Date(abolitionDate);
    return now > date ? t('tenants.status.abolished') : t('tenants.status.scheduledAbolition');
  };

  // Search effect for modal
  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (searchTerm || searchPrefecture || searchType) {
        try {
          const params = new URLSearchParams();
          if (searchTerm) params.append('q', searchTerm);
          if (searchPrefecture) params.append('prefecture', searchPrefecture);
          if (searchType) params.append('type', searchType);

          const res = await authGet(apiUrl(`/api/master/organizations?${params.toString()}`));
          if (res.ok) {
            setSearchResults((await res.json()) as MasterOrganization[]);
          }
        } catch (e) {
          console.error(e);
        }
      } else {
        setSearchResults([]);
      }
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, searchPrefecture, searchType]);

  if (isLoading) {
    return <div className="p-8 text-center text-gray-500">{t('common.loading')}</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2 text-gray-900">
            <Building2 className="w-7 h-7 text-gray-700" />
            {t('menu.institutions')}
          </h1>
          <p className="text-gray-500 mt-1">{t('institutions.description')}</p>
        </div>
        <Button onClick={() => setIsModalOpen(true)} className="gap-2">
          {t('tenants.buttons.addPartner')}
        </Button>
      </div>

      <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-sm space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder={t('institutions.filters.searchPlaceholder')}
              value={listSearch}
              onChange={(e) => setListSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
            />
          </div>
          <div>
            <select
              value={listType}
              onChange={(e) => setListType(e.target.value)}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm outline-none"
            >
              <option value="">{t('tenants.filters.allTypes')}</option>
              <option value="national">{t('tenants.types.national')}</option>
              <option value="public">{t('tenants.types.public')}</option>
              <option value="private">{t('tenants.types.private')}</option>
            </select>
          </div>
          <div className="relative">
            <PrefectureMultiSelect
              selectedCodes={listPrefectures}
              onToggle={togglePrefecture}
              onClear={() => {
                setListPrefectures([]);
                setCurrentPage(1);
              }}
              allLabel={t('tenants.filters.allPrefectures')}
            />
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Checkbox id="showAbolished" checked={showAbolished} onCheckedChange={(c) => setShowAbolished(!!c)} />
              <label htmlFor="showAbolished" className="text-sm font-medium text-gray-700 cursor-pointer">
                {t('tenants.filters.showAbolished')}
              </label>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => {
            setFilterMode('contracted');
            setCurrentPage(1);
          }}
          className={`px-4 py-1.5 text-xs font-bold rounded-md transition-all ${
            filterMode === 'contracted' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          {t('tenants.tabs.contracted')} ({contractedCount})
        </button>
        <button
          type="button"
          onClick={() => {
            setFilterMode('notContracted');
            setCurrentPage(1);
          }}
          className={`px-4 py-1.5 text-xs font-bold rounded-md transition-all ${
            filterMode === 'notContracted' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          {t('tenants.tabs.notContracted')} ({notContractedCount})
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50/50">
              <TableHead onClick={() => handleSort('name')} className="cursor-pointer group">
                <div className="flex items-center gap-1">
                  {t('tenants.table.institutionName')}
                  <SortIcon field="name" currentField={sortField} order={sortOrder} />
                </div>
              </TableHead>
              <TableHead onClick={() => handleSort('type')} className="cursor-pointer group">
                <div className="flex items-center gap-1">
                  {t('tenants.table.type')}
                  <SortIcon field="type" currentField={sortField} order={sortOrder} />
                </div>
              </TableHead>
              <TableHead className="text-center">{t('tenants.table.branch')}</TableHead>
              <TableHead onClick={() => handleSort('prefectureCode')} className="cursor-pointer group">
                <div className="flex items-center gap-1">
                  {t('tenants.table.prefecture')}
                  <SortIcon field="prefectureCode" currentField={sortField} order={sortOrder} />
                </div>
              </TableHead>
              <TableHead onClick={() => handleSort('establishmentDate')} className="cursor-pointer group text-[10px]">
                <div className="flex items-center gap-1">
                  {t('institutions.table.establishmentDate')}
                  <SortIcon field="establishmentDate" currentField={sortField} order={sortOrder} />
                </div>
              </TableHead>
              <TableHead>{t('tenants.table.abolition')}</TableHead>
              <TableHead className="text-right">{t('tenants.table.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedTenants.map((tenant) => (
              <TableRow key={tenant.id} className="hover:bg-gray-50/50 transition-colors">
                <TableCell className="py-4">
                  <div className="text-[10px] text-gray-400 font-bold mb-0.5">{tenant.tenant?.name || '---'}</div>
                  <Link to={`/system/tenants/${tenant.id}`} className="font-bold text-gray-900 hover:text-blue-600">
                    {tenant.name}
                  </Link>
                  <div className="text-[10px] text-gray-500 mt-1 flex items-center gap-1">
                    {tenant.founderName && (
                      <span className="text-blue-600 flex items-center gap-1">{tenant.founderName}</span>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <span
                    className={cn(
                      'inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold border',
                      getTenantTypeStyles(tenant.type),
                    )}
                  >
                    {t(`tenants.types.${tenant.type}`)}
                  </span>
                </TableCell>
                <TableCell className="text-center font-medium text-gray-600">
                  {t(`tenants.branchTypes.${tenant.mainBranchType?.toLowerCase() || 'main'}`)}
                </TableCell>
                <TableCell className="font-medium text-gray-700">
                  {getPrefectureName(tenant.prefectureCode) || tenant.prefectureCode}
                </TableCell>
                <TableCell className="text-[11px] text-gray-500 font-mono">
                  {tenant.establishmentDate || '---'}
                </TableCell>
                <TableCell>
                  <span
                    className={cn(
                      'text-[10px] font-bold px-2 py-0.5 rounded border',
                      (() => {
                        const status = getLifecycleStatus(tenant.abolitionDate);
                        if (status === t('tenants.status.abolished')) return 'bg-red-50 text-red-700 border-red-200';
                        if (status === t('tenants.status.scheduledAbolition'))
                          return 'bg-yellow-50 text-yellow-700 border-yellow-200';
                        return 'text-gray-400 border-transparent';
                      })(),
                    )}
                  >
                    {getLifecycleStatus(tenant.abolitionDate)}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-2">
                    {(() => {
                      if (tenant.tenantId && tenant.tenant?.isContracted === 'true') {
                        return (
                          <Button asChild variant="outline" size="sm" className="h-8 text-[11px] font-bold">
                            <Link to={`/system/tenants/${tenant.tenantId}`}>{t('tenants.buttons.edit')}</Link>
                          </Button>
                        );
                      }
                      if (tenant.tenantId) {
                        return (
                          <Button
                            asChild
                            variant="default"
                            size="sm"
                            className="h-8 text-[11px] font-bold bg-amber-600 hover:bg-amber-700"
                          >
                            <Link
                              to={`/system/tenants/wizard?id=${tenant.tenantId}&name=${encodeURIComponent(tenant.name)}`}
                            >
                              {t('institutions.buttons.initialSetup')}
                            </Link>
                          </Button>
                        );
                      }
                      return (
                        <Button
                          asChild
                          variant="ghost"
                          size="sm"
                          className="h-8 text-[11px] font-bold text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        >
                          <Link to={`/system/institutions/${tenant.id}`}>{t('institutions.buttons.editMaster')}</Link>
                        </Button>
                      );
                    })()}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <div className="p-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
          <div className="text-sm text-gray-600">{t('tenants.pagination.total', { count: totalItems })}</div>
          <div className="flex items-center gap-4">
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="border border-gray-300 rounded px-2 py-1 text-sm bg-white outline-none"
            >
              {PAGE_SIZES.map((size) => (
                <option key={size} value={size}>
                  {t('tenants.pagination.perPage', { count: size })}
                </option>
              ))}
            </select>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                className="h-8"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                {t('common.previous')}
              </Button>
              <span className="px-3 py-1 text-sm font-medium">
                {currentPage} / {totalPages || 1}
              </span>
              <Button
                variant="outline"
                size="sm"
                className="h-8"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
              >
                {t('common.next')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[85vh]">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
              <h2 className="font-semibold text-lg text-gray-800">{t('tenants.modal.addPartner')}</h2>
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 p-1 hover:bg-gray-200 rounded-full transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 border-b border-gray-100 space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder={t('tenants.modal.searchPlaceholder')}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <select
                  className="p-2 border border-gray-300 rounded-lg text-sm"
                  value={searchPrefecture}
                  onChange={(e) => setSearchPrefecture(e.target.value)}
                >
                  <option value="">{t('tenants.filters.allPrefectures')}</option>
                  {PREFECTURE_OPTIONS.map(({ code }) => (
                    <option key={code} value={code}>
                      {t(`tenants.prefectures.${code}`)}
                    </option>
                  ))}
                </select>
                <select
                  className="p-2 border border-gray-300 rounded-lg text-sm"
                  value={searchType}
                  onChange={(e) => setSearchType(e.target.value)}
                >
                  <option value="">{t('tenants.filters.allTypes')}</option>
                  <option value="national">{t('tenants.types.national')}</option>
                  <option value="public">{t('tenants.types.public')}</option>
                  <option value="private">{t('tenants.types.private')}</option>
                </select>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 bg-gray-50/30">
              <div className="space-y-2">
                {searchResults.map((univ) => (
                  <button
                    type="button"
                    key={univ.id}
                    onClick={() => setSelectedCandidate(univ.id)}
                    className={cn(
                      'w-full p-4 rounded-lg border text-left transition-all flex items-center justify-between group',
                      selectedCandidate === univ.id
                        ? 'bg-blue-50 border-blue-500 ring-1 ring-blue-500'
                        : 'bg-white border-gray-200 hover:border-blue-300',
                    )}
                  >
                    <div>
                      <div className="font-bold text-gray-900 flex items-center gap-2">
                        {univ.name}
                        <span
                          className={cn(
                            'text-[10px] px-1.5 py-0.5 rounded border uppercase',
                            getCandidateTypeStyles(univ.type),
                          )}
                        >
                          {t(`tenants.types.${univ.type}`)}
                        </span>
                      </div>
                    </div>
                    <div
                      className={cn(
                        'w-6 h-6 rounded-full flex items-center justify-center border transition-colors',
                        selectedCandidate === univ.id
                          ? 'bg-blue-600 border-blue-600 text-white'
                          : 'border-gray-300 text-transparent group-hover:border-blue-400',
                      )}
                    >
                      <Check className="w-4 h-4" />
                    </div>
                  </button>
                ))}
                {searchTerm && searchResults.length === 0 && (
                  <div className="text-center py-12 text-gray-500">{t('tenants.modal.noResults')}</div>
                )}
                {!searchTerm && <div className="text-center py-12 text-gray-400">{t('tenants.modal.startTyping')}</div>}
              </div>
            </div>

            <div className="p-4 border-t border-gray-100 bg-white flex justify-end gap-3">
              <Button variant="ghost" onClick={() => setIsModalOpen(false)}>
                {t('tenants.modal.cancel')}
              </Button>
              <Button disabled={!selectedCandidate} asChild={!!selectedCandidate} className="font-bold gap-2 px-8">
                {selectedCandidate ? (
                  <Link
                    to={`/system/tenants/wizard?id=${selectedCandidate}&name=${encodeURIComponent(searchResults.find((r) => r.id === selectedCandidate)?.name || '')}`}
                  >
                    {t('tenants.modal.continue')} <ArrowRight className="w-4 h-4" />
                  </Link>
                ) : (
                  <span>
                    {t('tenants.modal.continue')} <ArrowRight className="w-4 h-4" />
                  </span>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Multi-select dropdown for prefecture filtering, sorted by code.
 * @param props - Component props
 * @returns Prefecture multi-select component
 */
function PrefectureMultiSelect({
  selectedCodes,
  onToggle,
  onClear,
  allLabel,
}: Readonly<{
  selectedCodes: string[];
  onToggle: (code: string) => void;
  onClear: () => void;
  allLabel: string;
}>) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const label =
    selectedCodes.length === 0
      ? allLabel
      : selectedCodes
          .slice()
          .sort()
          .map((c) => t(`tenants.prefectures.${c}`))
          .join(', ');

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm outline-none text-left flex items-center justify-between bg-white"
      >
        <span className="truncate">{label}</span>
        <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
      </button>
      {isOpen && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-10 cursor-default bg-transparent border-none"
            onClick={() => setIsOpen(false)}
            aria-label="Close dropdown"
          />
          <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {selectedCodes.length > 0 && (
              <button
                type="button"
                onClick={() => {
                  onClear();
                  setIsOpen(false);
                }}
                className="w-full px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 text-left border-b border-gray-100"
              >
                {t('common.clearFilter')}
              </button>
            )}
            {PREFECTURE_OPTIONS.map(({ code }) => (
              <button
                type="button"
                key={code}
                onClick={() => onToggle(code)}
                className="w-full px-4 py-1.5 text-sm hover:bg-gray-50 text-left flex items-center gap-2"
              >
                <div
                  className={cn(
                    'w-4 h-4 rounded border flex items-center justify-center text-white',
                    selectedCodes.includes(code) ? 'bg-blue-600 border-blue-600' : 'border-gray-300',
                  )}
                >
                  {selectedCodes.includes(code) && <Check className="w-3 h-3" />}
                </div>
                {t(`tenants.prefectures.${code}`)}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function SortIcon({
  field,
  currentField,
  order,
}: Readonly<{ field: SortField; currentField: SortField; order: SortOrder }>) {
  if (currentField !== field)
    return <ArrowUpDown className="w-3 h-3 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity" />;
  return order === 'asc' ? (
    <ChevronUp className="w-3 h-3 text-blue-600" />
  ) : (
    <ChevronDown className="w-3 h-3 text-blue-600" />
  );
}
